import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../Components/Header'

const DeviceManagement = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    username: '',
    device_id: '',
    project_id: '',
    project_name: '',
    location_id: '',
    imei: '',
    timezone: '',
    mobile_no: '',
    latitude: 0,
    longitude: 0,
    place: '',
    city: '',
    date: '',
    country: '',
    parameters: []
  });

  // State for parameter fields
  const [parameter, setParameter] = useState({
    parameter_name: '',
    parameter_type: '',
    unit: '',
    position: 1
  });

  // State for error and success messages
  const [message, setMessage] = useState({ text: '', type: '' });
  const [usernames, setUsernames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('deviceInfo');

  // Fetch usernames when component mounts
  useEffect(() => {
    const fetchUsernames = async () => {
      try {
        const response = await axios.get('/api/usernames/');
        setUsernames(response.data);
      } catch (error) {
        console.error('Error fetching usernames:', error);
        setMessage({ text: 'Failed to fetch usernames', type: 'error' });
      }
    };

    fetchUsernames();
  }, []);

  // Handle changes to main form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle changes to latitude and longitude (number fields)
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0
    });
  };

  // Handle changes to parameter fields
  const handleParameterChange = (e) => {
    const { name, value } = e.target;
    setParameter({
      ...parameter,
      [name]: name === 'position' ? parseInt(value, 10) || 1 : value
    });
  };

  // Add a parameter to the list
  const addParameter = () => {
    if (!parameter.parameter_name || !parameter.parameter_type || !parameter.unit) {
      setMessage({ text: 'Please fill all parameter fields', type: 'error' });
      return;
    }

    setFormData({
      ...formData,
      parameters: [...formData.parameters, { ...parameter }]
    });

    // Reset parameter form
    setParameter({
      parameter_name: '',
      parameter_type: '',
      unit: '',
      position: formData.parameters.length + 1
    });

    setMessage({ text: 'Parameter added successfully', type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Remove a parameter from the list
  const removeParameter = (index) => {
    const updatedParameters = [...formData.parameters];
    updatedParameters.splice(index, 1);
    
    // Update positions
    const reindexedParameters = updatedParameters.map((param, idx) => ({
      ...param,
      position: idx + 1
    }));
    
    setFormData({
      ...formData,
      parameters: reindexedParameters
    });
  };

  // Submit the form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    // Validate form
    if (!formData.username || !formData.device_id) {
      setMessage({ text: 'Username and Device ID are required', type: 'error' });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/create_device/', formData);
      setMessage({ text: response.data.message, type: 'success' });
      
      // Reset form after successful submission
      setFormData({
        username: '',
        device_id: '',
        project_id: '',
        project_name: '',
        location_id: '',
        imei: '',
        timezone: '',
        mobile_no: '',
        latitude: 0,
        longitude: 0,
        place: '',
        city: '',
        date: '',
        country: '',
        parameters: []
      });
    } catch (error) {
      console.error('Error creating device:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create device';
      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Header/>
   
    <div className="min-h-screen ">
      <div className="max-w-4xl mx-auto p-6">
        
        {/* Message display */}
        {message.text && (
          <div 
            className={`p-4 mb-6 rounded-lg shadow-md flex items-center ${
              message.type === 'error' 
                ? 'bg-red-50 border-l-4 border-red-500 text-red-700' 
                : 'bg-green-50 border-l-4 border-green-500 text-green-700'
            }`}
          >
            <span className={`mr-2 text-2xl ${message.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>
              {message.type === 'error' ? '⚠' : '✓'}
            </span>
            <span className="font-medium">{message.text}</span>
          </div>
        )}
        
        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button 
              className={`py-4 px-6 font-medium text-lg ${
                activeTab === 'deviceInfo' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-blue-500'
              }`}
              onClick={() => setActiveTab('deviceInfo')}
            >
              Device Information
            </button>
            <button 
              className={`py-4 px-6 font-medium text-lg ${
                activeTab === 'parameters' 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-blue-500'
              }`}
              onClick={() => setActiveTab('parameters')}
            >
              Parameters
              {formData.parameters.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {formData.parameters.length}
                </span>
              )}
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            {/* Device Info Tab */}
            {activeTab === 'deviceInfo' && (
              <div className="space-y-6">
                
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  {/* User selection */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Username <span className="text-red-500">*</span></label>
                    <select 
                      name="username" 
                      value={formData.username} 
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      required
                    >
                      <option value="">Select a user</option>
                      {usernames.map(username => (
                        <option key={username} value={username}>{username}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Device ID */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Device ID <span className="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      name="device_id" 
                      value={formData.device_id} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      required
                      placeholder="Enter unique device identifier"
                    />
                  </div>
                  
                  {/* Project Details Section */}
                  <div className="col-span-1 md:col-span-2 mt-4 border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Project Details</h3>
                  </div>
                  
                  {/* Project ID */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Project ID</label>
                    <input 
                      type="text" 
                      name="project_id" 
                      value={formData.project_id} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter project identifier"
                    />
                  </div>
                  
                  {/* Project Name */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Project Name</label>
                    <input 
                      type="text" 
                      name="project_name" 
                      value={formData.project_name} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter project name"
                    />
                  </div>
                  
                  {/* Device Details Section */}
                  <div className="col-span-1 md:col-span-2 mt-4 border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Device Details</h3>
                  </div>
                  
                  {/* Location ID */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Location ID</label>
                    <input 
                      type="text" 
                      name="location_id" 
                      value={formData.location_id} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter location identifier"
                    />
                  </div>
                  
                  {/* IMEI */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">IMEI</label>
                    <input 
                      type="text" 
                      name="imei" 
                      value={formData.imei} 
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter device IMEI number"
                    />
                  </div>
                  
                  {/* Timezone */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Timezone</label>
                    <input 
                      type="text" 
                      name="timezone" 
                      value={formData.timezone} 
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="e.g. UTC+05:30"
                    />
                  </div>
                  
                  {/* Mobile Number */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Mobile Number</label>
                    <input 
                      type="text" 
                      name="mobile_no" 
                      value={formData.mobile_no} 
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter contact number"
                    />
                  </div>
                  
                  {/* Location Details Section */}
                  <div className="col-span-1 md:col-span-2 mt-4 border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Location Details</h3>
                  </div>
                  
                  {/* Latitude */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Latitude</label>
                    <input 
                      type="number" 
                      step="0.000001" 
                      name="latitude" 
                      value={formData.latitude} 
                      onChange={handleNumberChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="e.g. 28.613939"
                    />
                  </div>
                  
                  {/* Longitude */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Longitude</label>
                    <input 
                      type="number" 
                      step="0.000001" 
                      name="longitude" 
                      value={formData.longitude} 
                      onChange={handleNumberChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="e.g. 77.209021"
                    />
                  </div>
                  
                  {/* Place */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Place</label>
                    <input 
                      type="text" 
                      name="place" 
                      value={formData.place} 
                      onChange={handleChange}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter specific location"
                    />
                  </div>
                  
                  {/* City */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">City</label>
                    <input 
                      type="text" 
                      name="city" 
                      value={formData.city} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter city name"
                    />
                  </div>
                  
                  {/* Date */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Date</label>
                    <input 
                      type="date" 
                      name="date" 
                      value={formData.date} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Country</label>
                    <input 
                      type="text" 
                      name="country" 
                      value={formData.country} 
                      onChange={handleChange}
                      className=" p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter country name"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button 
                    type="button"
                    onClick={() => setActiveTab('parameters')}
                    className="px-6 py-3 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 font-medium transition-all duration-200"
                  >
                    Next: Add Parameters
                  </button>
                </div>
              </div>
            )}
            
            {/* Parameters Tab */}
            {activeTab === 'parameters' && (
              <div>
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Device Parameters</h3>
                  <p className="text-blue-600 text-sm">Add measurement parameters for your weather station device</p>
                </div>
                
                {/* Parameters table */}
                {formData.parameters.length > 0 ? (
                  <div className="mb-6 overflow-x-auto rounded-lg border border-gray-200 shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.parameters.map((param, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.parameter_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.parameter_type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.unit}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{param.position}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button 
                                type="button"
                                onClick={() => removeParameter(index)}
                                className="text-red-600 hover:text-red-900 font-medium"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mb-6 p-8 bg-gray-50 rounded-lg text-center border border-dashed border-gray-300">
                    <p className="text-gray-500 mb-2">No parameters added yet</p>
                    <p className="text-sm text-gray-400">Use the form below to add measurement parameters</p>
                  </div>
                )}
                
                {/* Add parameter form */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
                  <h4 className="text-lg font-medium text-gray-800 mb-4">Add New Parameter</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block mb-2 font-medium text-gray-700">Parameter Name</label>
                      <input 
                        type="text" 
                        name="parameter_name" 
                        value={parameter.parameter_name} 
                        onChange={handleParameterChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g. Temperature"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 font-medium text-gray-700">Parameter Type</label>
                      <input 
                        type="text" 
                        name="parameter_type" 
                        value={parameter.parameter_type} 
                        onChange={handleParameterChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g. Numeric"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 font-medium text-gray-700">Unit</label>
                      <input 
                        type="text" 
                        name="unit" 
                        value={parameter.unit} 
                        onChange={handleParameterChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g. °C"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <button 
                      type="button"
                      onClick={addParameter}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-medium"
                    >
                      Add Parameter
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-between gap-3 mt-6 pt-4 border-t">
                  <button 
                    type="button"
                    onClick={() => setActiveTab('deviceInfo')}
                    className="px-6 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium transition-all duration-200"
                  >
                    Back to Device Info
                  </button>
                  
                  <button 
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium transition-all duration-200 flex items-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Create Device'
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
        
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Weather Station Management System © 2025</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default DeviceManagement;